import type { ScopeType } from "../types/RolesPermissions.type";

interface ScopeTypeStyle {
	/** Human label for the scope type, as the product names it. */
	label: string;
	/** Tailwind classes for a pill/chip carrying a scope of this type. */
	chip: string;
}

/**
 * One colour per scope type, shared by every surface that shows a specific
 * allocation.
 *
 * Product asked for these three to be distinguishable at a glance — Portfolio
 * green, Project yellow, RFx blue — and they were previously coloured ad hoc:
 * the assign wizard used teal for RFx and the SAME blue for both portfolios and
 * projects, which made the two hardest-to-tell-apart types identical. Colour is
 * doing real work here, so it lives in one place rather than at each call site.
 *
 * `global` stays deliberately neutral: it is not one of the three specific
 * levels, and giving it a colour of its own would imply it belongs to the same
 * scale.
 */
export const SCOPE_TYPE_STYLE: Record<ScopeType, ScopeTypeStyle> = {
	global: { label: "Company-wide", chip: "border-gray-300 bg-gray-50 text-gray-700" },
	project_type: { label: "Portfolio", chip: "border-emerald-300 bg-emerald-50 text-emerald-800" },
	project: { label: "Project", chip: "border-amber-300 bg-amber-50 text-amber-900" },
	rfx: { label: "RFx", chip: "border-blue-300 bg-blue-50 text-blue-800" },
};

export const scopeTypeStyle = (type: string): ScopeTypeStyle =>
	SCOPE_TYPE_STYLE[type as ScopeType] ?? {
		label: type,
		chip: "border-gray-300 bg-gray-50 text-gray-700",
	};
